import { useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  useAddDraftStep,
  useAttachDraftEvent,
  useConnectDraftSteps,
  useDeleteDraftStep,
  useDetachDraftEvent,
  useDisconnectDraftSteps,
  useUpdateDraftStep,
  useUpgradeLegacyEventInput
} from '@/api/mutations/useProcessDraft';
import { runDraftTransaction } from '@/api/mutations/helpers/runDraftTransaction';
import { useDraftGraph } from '@/api/draftGraph';
import type { EdgeActionDeps } from '../edge-actions/types';
import { useProcessDraftCache } from './useProcessDraftCache';

interface UseEdgeActionDepsArgs {
  processId: string;
  /**
   * See `EdgeActionDeps.getAssigneeLookup`. Forwarded through here so the
   * editor page's existing lookup query results are reused instead of
   * re-fetching org-members in this hook.
   */
  getAssigneeLookup?: EdgeActionDeps['getAssigneeLookup'];
}

export function useEdgeActionDeps({
  processId,
  getAssigneeLookup
}: UseEdgeActionDepsArgs): EdgeActionDeps {
  const queryClient = useQueryClient();
  const graph = useDraftGraph(processId);
  const addStepMutation = useAddDraftStep(processId);
  const deleteStepMutation = useDeleteDraftStep(processId);
  const connectMutation = useConnectDraftSteps(processId);
  const disconnectMutation = useDisconnectDraftSteps(processId);
  const updateStepMutation = useUpdateDraftStep(processId);
  const attachEventMutation = useAttachDraftEvent(processId);
  const detachEventMutation = useDetachDraftEvent(processId);
  const upgradeLegacyEventMutation = useUpgradeLegacyEventInput(processId);
  const {
    getCachedSteps,
    getCachedDraftAssignments,
    getCachedEvents,
    getCachedGeneratedPositions,
    getCachedStepPositions
  } = useProcessDraftCache(processId);

  const addStepAsync = addStepMutation.mutateAsync;
  const updateStepAsync = updateStepMutation.mutateAsync;
  const deleteStepAsync = deleteStepMutation.mutateAsync;
  const connectAsync = connectMutation.mutateAsync;
  const disconnectAsync = disconnectMutation.mutateAsync;
  const attachEventAsync = attachEventMutation.mutateAsync;
  const detachEventAsync = detachEventMutation.mutateAsync;
  const upgradeLegacyEventAsync = upgradeLegacyEventMutation.mutateAsync;

  return useMemo<EdgeActionDeps>(
    () => ({
      processId,
      queryClient,
      graph,
      addStep: (position, laneKey) =>
        addStepAsync({
          position,
          ...(laneKey ? { laneKey } : {})
        }),
      updateStep: ({ stepId, patch }) =>
        updateStepAsync({
          stepId: graph.resolveNodeRef(stepId, 'step').backendId,
          patch
        }),
      deleteStep: (stepId) => deleteStepAsync(graph.resolveNodeRef(stepId, 'step').backendId),
      connect: (sourceStepId, targetStepId, ruleId) =>
        connectAsync({
          stepId: graph.resolveNodeRef(sourceStepId, 'step').backendId,
          body: {
            targetStepId: graph.resolveNodeRef(targetStepId, ['step', 'assignment']).backendId,
            ...(ruleId ? { ruleId } : {})
          }
        }),
      disconnect: (sourceStepId, targetStepId, ruleId) =>
        disconnectAsync({
          stepId: graph.resolveNodeRef(sourceStepId, 'step').backendId,
          targetStepId: graph.resolveNodeRef(targetStepId, ['step', 'assignment']).backendId,
          ...(ruleId ? { ruleId } : {})
        }),
      attachEvent: ({ eventId, targetStepId }) =>
        attachEventAsync({ eventId, body: { targetStepId } }),
      detachEvent: ({ eventId, stepId }) => detachEventAsync({ eventId, stepId }),
      upgradeLegacyEventInput: ({ stepId, inputIndex }) =>
        upgradeLegacyEventAsync({ stepId, inputIndex }),
      getCachedSteps,
      getCachedDraftAssignments,
      getCachedEvents,
      getCachedGeneratedPositions,
      getPositionsById: getCachedStepPositions,
      runTransaction: (args) => runDraftTransaction({ ...args, queryClient, processId }),
      getAssigneeLookup
    }),
    [
      addStepAsync,
      updateStepAsync,
      deleteStepAsync,
      connectAsync,
      disconnectAsync,
      attachEventAsync,
      detachEventAsync,
      upgradeLegacyEventAsync,
      getCachedSteps,
      getCachedDraftAssignments,
      getCachedEvents,
      getCachedGeneratedPositions,
      getCachedStepPositions,
      queryClient,
      processId,
      graph,
      getAssigneeLookup
    ]
  );
}
